import { useEffect, useState, type FormEvent } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TASK_CATEGORIES,
  STATUS_COLUMNS,
  type Priority,
  type Status,
} from "./directory-data";
import {
  usePeople,
  useVendors,
  useCreatePerson,
  useCreateVendor,
} from "./use-directory";
import {
  useDeleteTask,
  useUploadAttachment,
  type EditableTask,
} from "./use-tasks";
import { CreatableMultiSelect } from "./CreatableMultiSelect";
import { AttachmentsManager } from "./AttachmentsManager";

type Defaults = {
  status?: Status;
  priority?: Priority;
  category?: string;
  vendor_ids?: (number | string)[];
  person_ids?: (number | string)[];
};

export function AddTaskDialog({
  open,
  onOpenChange,
  defaults,
  task,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: Defaults;
  task?: EditableTask;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const peopleQ = usePeople();
  const vendorsQ = useVendors();
  const createPerson = useCreatePerson();
  const createVendor = useCreateVendor();
  const deleteTask = useDeleteTask();
  const uploadAttachment = useUploadAttachment();

  const isEdit = !!task;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<Status>("todo");
  const [priority, setPriority] = useState<Priority>("medium");
  const [category, setCategory] = useState("Operations");
  const [vendorIds, setVendorIds] = useState<(number | string)[]>([]);
  const [personIds, setPersonIds] = useState<(number | string)[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(task?.title ?? "");
    setDescription(task?.description ?? "");
    setStatus((task?.status as Status | undefined) ?? defaults?.status ?? "todo");
    setPriority(
      (task?.priority as Priority | undefined) ?? defaults?.priority ?? "medium",
    );
    setCategory(task?.category ?? defaults?.category ?? "Operations");
    setVendorIds(task?.vendors?.map((v) => v.id) ?? defaults?.vendor_ids ?? []);
    setPersonIds(task?.people?.map((p) => p.id) ?? defaults?.person_ids ?? []);
    setDueDate(task?.due_date ?? "");
    setPendingFiles([]);
    setError(null);
    setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const url = isEdit
        ? `/api/operations_tasks/${task!.id}`
        : `/api/operations_tasks`;
      const method = isEdit ? "PATCH" : "POST";
      const res = await api(url, {
        method,
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          status,
          priority,
          category,
          vendor_ids: vendorIds,
          person_ids: personIds,
          due_date: dueDate || null,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      const savedTask = (await res.json()) as { id?: number | string };

      // Upload any pending files (only relevant in create mode — edit-mode
      // attachments go through AttachmentsManager directly).
      if (!isEdit && pendingFiles.length > 0 && savedTask?.id !== undefined) {
        const newId = savedTask.id;
        const failures: string[] = [];
        for (const file of pendingFiles) {
          try {
            await uploadAttachment.mutateAsync({ taskId: newId, file });
          } catch (e) {
            failures.push(
              `${file.name}: ${e instanceof Error ? e.message : String(e)}`,
            );
          }
        }
        if (failures.length > 0) {
          window.alert(
            `Task created, but ${failures.length} attachment(s) failed:\n\n${failures.join("\n")}\n\nReopen the task to retry.`,
          );
        }
      }

      queryClient.invalidateQueries({ queryKey: ["operations_tasks"] });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDelete() {
    if (!task) return;
    const ok = window.confirm(
      `Delete "${task.title}"?\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    setError(null);
    deleteTask.mutate(task.id, {
      onSuccess: () => onOpenChange(false),
      onError: (err) =>
        setError(err instanceof Error ? err.message : String(err)),
    });
  }

  const peopleOpts = (peopleQ.data ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    chip_color: p.chip_color,
  }));
  const vendorOpts = (vendorsQ.data ?? []).map((v) => ({
    id: v.id,
    name: v.name,
    chip_color: v.chip_color,
  }));

  const busy = isSubmitting || deleteTask.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "New task"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to happen?"
              autoFocus
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-description">Description</Label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as Status)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_COLUMNS.map((col) => (
                    <SelectItem key={col.id} value={col.id}>
                      {col.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Priority</Label>
              <Select
                value={priority}
                onValueChange={(v) => setPriority(v as Priority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Vendors</Label>
            <CreatableMultiSelect
              label="Vendor"
              placeholder="Add vendors…"
              options={vendorOpts}
              selectedIds={vendorIds}
              onChange={setVendorIds}
              onCreate={(name) => createVendor.mutateAsync(name)}
              isCreating={createVendor.isPending}
              disabled={vendorsQ.isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>People</Label>
            <CreatableMultiSelect
              label="Person"
              placeholder="Add people…"
              options={peopleOpts}
              selectedIds={personIds}
              onChange={setPersonIds}
              onCreate={(name) => createPerson.mutateAsync(name)}
              isCreating={createPerson.isPending}
              disabled={peopleQ.isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-due">Due date</Label>
            <Input
              id="task-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Attachments</Label>
            {isEdit && task ? (
              <AttachmentsManager taskId={task.id} />
            ) : (
              <AttachmentsManager
                pendingFiles={pendingFiles}
                onPendingFilesChange={setPendingFiles}
              />
            )}
          </div>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}

          <DialogFooter className="sm:justify-between gap-2">
            {isEdit ? (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={busy}
              >
                {deleteTask.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !title.trim()}>
                {isSubmitting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {isEdit ? "Save changes" : "Create task"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
